import { useMutation } from '@apollo/client';
import type { InsertEntityMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertEntityDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertEntityTRPCInput } from './useInsertEntityTRPC';
import { useInsertEntityTRPC } from './useInsertEntityTRPC';

export const useInsertEntity = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation (legacy)
  const [insertEntityGraphQL, graphqlState] = useMutation(
    InsertEntityDocument,
    {
      update: (cache) => {
        evictField(cache, 'entity');
        evictField(cache, 'entity_by_pk');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertEntityTRPC();

  const insertEntity = async (
    variables: InsertEntityTRPCInput
  ): Promise<InsertEntityMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertEntity(variables);
    }

    const result = await insertEntityGraphQL({
      variables: {
        Name: variables.Name,
        Description: variables.Description,
        ParentId: variables.ParentId,
        Weight: variables.Weight,
        owners: variables.OwnerUserIds ?? [],
        ownerGroups: variables.OwnerGroupIds ?? [],
      },
    });
    if (!result.data) {
      throw new Error('Failed to insert entity');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      insertEntity,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertEntity,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
